import { useSceneStyles } from './styles';
import { useStyles } from '../HotspotSelector/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { HotspotSelector } from '../HotspotSelector';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';

const SceneControls = ({
    showAddInfoHotspot,
    hotspotIcons,
    isNotUser,
    addInfoHotspotLabel,
    handleSceneInfoClick,
    asset,
    setSelectedHotspotIcons,
    selectedHotspotIcons,
    defaultItemHotspot,
    itemHotspots,
    handleMultipleHotspot,
    isMultipleHotspotEnabled,
    handleSceneNavClick,
    defaultNavHotspot,
    navigationHotspots,
    handleSceneInitViewClick,
    handleSceneResetViewClick,
    orbitMode = false,
    initialViewOnly = false, // <-- new opt-in prop
}) => {
    const theme = useSelector(selectDefaultTheme);
    const config = useSelector(getConfig);
    const classes = useSceneStyles(theme);
    const hotspotBtnClasses = useStyles(theme);

    const emitNodeInitialViewEvent = (initialView, clearInitialView = false) => {
        try {
            window.dispatchEvent(
                new CustomEvent('gemini:node-initial-view', {
                    detail: {
                        initial_view: initialView,
                        clear_initial_view: clearInitialView,
                        asset_id: asset?.asset_id ?? null,
                        zone_id: asset?.active_zone_id ?? null,
                    },
                })
            );
        } catch (e) {
            /* no-op */
        }
    };

    // If consumer requested minimal controls for initial-view-only flows, render only those buttons.
    if (initialViewOnly) {
        return (
            <div className={classes.sceneControls}>
                <Tooltip title="Set the initial location and zoom level for this node to the current view.">
                    <div
                        className={hotspotBtnClasses.sceneContainer}
                        onClick={() => {
                            const viewParams =
                                typeof window !== 'undefined' &&
                                window.geminiViewManager &&
                                typeof window.geminiViewManager.getCurrentViewParams === 'function'
                                    ? window.geminiViewManager.getCurrentViewParams()
                                    : null;
                            emitNodeInitialViewEvent(viewParams, false);
                            handleSceneInitViewClick && handleSceneInitViewClick(viewParams);
                        }}
                    >
                        <div className={hotspotBtnClasses.initialViewIconWrap}>
                            <img
                                src={config.paths.SCENE_INITVIEW_ICON_URL}
                                alt="Set Scene Initial View"
                                className={hotspotBtnClasses.infoIcon}
                            ></img>
                        </div>
                        <div className={hotspotBtnClasses.sceneButton}>Set View for this Node</div>
                    </div>
                </Tooltip>
                <Tooltip title="Reset the initial location and zoom level for this node back to the default.">
                    <div
                        className={hotspotBtnClasses.sceneContainer}
                        onClick={() => {
                            emitNodeInitialViewEvent(null, true);
                            handleSceneResetViewClick && handleSceneResetViewClick();
                        }}
                    >
                        <div className={hotspotBtnClasses.initialViewIconWrap}>
                            <img
                                src={config.paths.SCENE_INITVIEW_ICON_URL}
                                alt="Reset Scene Initial View"
                                className={hotspotBtnClasses.infoIcon}
                            ></img>
                        </div>
                        <div className={hotspotBtnClasses.sceneButton}>Reset Initial View</div>
                    </div>
                </Tooltip>
            </div>
        );
    }

    // existing full UI
    return (
        <div className={classes.sceneControls}>
            {showAddInfoHotspot === true && hotspotIcons.length > 0 && isNotUser && (
                <HotspotSelector
                    buttonLabel={addInfoHotspotLabel}
                    buttonAction={handleSceneInfoClick}
                    srcImgId={asset.item_hotspot_icon_id}
                    scrImgAlt={'Add Scene Info Hotspot'}
                    selectedHotspotIconId={selectedHotspotIcons.itemIconId}
                    updateIconForSave={(hotspotIconId) => {
                        setSelectedHotspotIcons({
                            ...selectedHotspotIcons,
                            itemIconId: hotspotIconId,
                        });
                    }}
                    defaultHotspot={defaultItemHotspot}
                    hotspotIcons={itemHotspots}
                    enableMultipleHotspot
                    handleMultipleHotspot={handleMultipleHotspot}
                    isMultipleHotspotEnabled={isMultipleHotspotEnabled}
                    selectTooltip="Select item icon"
                    placeTooltip="Place item"
                    placeMultipleTooltip="Place multiple items"
                />
            )}
            {hotspotIcons.length > 0 && isNotUser && (
                <HotspotSelector
                    buttonLabel={'Navigation'}
                    buttonAction={handleSceneNavClick}
                    srcImgId={asset.nav_hotspot_icon_id}
                    scrImgAlt={'Add Scene Navigation Hotspot'}
                    selectedHotspotIconId={selectedHotspotIcons.navLinkIconId}
                    updateIconForSave={(hotspotIconId) => {
                        setSelectedHotspotIcons({
                            ...selectedHotspotIcons,
                            navLinkIconId: hotspotIconId,
                        });
                    }}
                    defaultHotspot={defaultNavHotspot}
                    hotspotIcons={navigationHotspots}
                    selectTooltip="Select navigation icon"
                    placeTooltip="Place navigation link"
                    placeMultipleTooltip="Place multiple navigation links"
                />
            )}
            {orbitMode === false && (
                <>
                    <Tooltip title="Set the initial location and zoom level for this zone to the current view.">
                        <div
                            className={hotspotBtnClasses.sceneContainer}
                            onClick={() => {
                                // get current view from viewer if available
                                const viewParams =
                                    typeof window !== 'undefined' &&
                                    window.geminiViewManager &&
                                    typeof window.geminiViewManager.getCurrentViewParams === 'function'
                                        ? window.geminiViewManager.getCurrentViewParams()
                                        : null;

                                // Emit event so embedded editors (ContentNodeForm) can capture yaw/pitch/zoom
                                try {
                                    window.dispatchEvent(
                                        new CustomEvent('gemini:node-initial-view', {
                                            detail: {
                                                initial_view: viewParams,
                                                asset_id: asset?.asset_id ?? null,
                                                zone_id: asset?.active_zone_id ?? null,
                                            },
                                        })
                                    );
                                } catch (e) {
                                    /* no-op */
                                }

                                // still call parent handler for existing flows (viewer-level behavior)
                                handleSceneInitViewClick && handleSceneInitViewClick(viewParams);
                            }}
                        >
                            <div className={hotspotBtnClasses.initialViewIconWrap}>
                                <img
                                    src={config.paths.SCENE_INITVIEW_ICON_URL}
                                    alt="Set Scene Initial View"
                                    className={hotspotBtnClasses.infoIcon}
                                ></img>
                            </div>
                            <div className={hotspotBtnClasses.sceneButton}>Set Initial View</div>
                        </div>
                    </Tooltip>
                    <Tooltip title="Reset the initial location and zoom level for this zone back to the default.">
                        <div className={hotspotBtnClasses.sceneContainer} onClick={handleSceneResetViewClick}>
                            <div className={hotspotBtnClasses.initialViewIconWrap}>
                                <img
                                    src={config.paths.SCENE_INITVIEW_ICON_URL}
                                    alt="Reset Scene Initial View"
                                    className={hotspotBtnClasses.infoIcon}
                                ></img>
                            </div>
                            <div className={hotspotBtnClasses.sceneButton}>Reset Initial View</div>
                        </div>
                    </Tooltip>
                </>
            )}
            {orbitMode === true && (
                <Tooltip title="Save the current zoom level as the initial view for all orbit frames.">
                    <div className={hotspotBtnClasses.sceneContainer} onClick={handleSceneInitViewClick}>
                        <div className={hotspotBtnClasses.initialViewIconWrap}>
                            <img
                                src={config.paths.SCENE_INITVIEW_ICON_URL}
                                alt="Set Orbit Initial Zoom"
                                className={hotspotBtnClasses.infoIcon}
                            ></img>
                        </div>
                        <div className={hotspotBtnClasses.sceneButton}>Set Initial Zoom</div>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export { SceneControls };
